import React, { useEffect } from "react";
import { useMutation } from "@apollo/client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Paper from "@mui/material/Paper";
import sumBy from "lodash/sumBy";
import format from "date-fns/format";
import { convertTimeToString, formatCurrency } from "common/format";
import { GET_TRANSACTION_IMAGES, GetTransactionImagesData, GetTransactionImagesVars } from "common/graphql";
import { LocalLevelError } from "../common/LocalLevelError";
import { ImageCarousel } from "../ImageCarousel";
import type { CarouselSlide } from "../ImageCarousel";
import type { TransactionData, BasicInvoiceData } from "common/graphql/fragments/invoiceFragment";

function getVehicleStatus(accounts: BasicInvoiceData["registered_vehicle"]["accounts"]): "Registered" | "Unregistered" {
  return accounts?.some(({ attachment_type }) => !attachment_type.includes("non_registration"))
    ? "Registered"
    : "Unregistered";
}

export interface InvoiceParkEventCardProps {
  transaction: TransactionData;
  registeredVehicle: BasicInvoiceData["registered_vehicle"];
  onClickViewDetails: () => void;
}

export const InvoiceParkEventCard: React.FC<InvoiceParkEventCardProps> = ({
  transaction,
  registeredVehicle,
  onClickViewDetails,
}) => {
  const [getTransactionImages, { data: imagesData, loading: imagesLoading, error: imagesError }] = useMutation<
    GetTransactionImagesData,
    GetTransactionImagesVars
  >(GET_TRANSACTION_IMAGES, {
    onError(err) {
      console.error("failed to fetch transaction images", err);
    },
  });

  useEffect(() => {
    getTransactionImages({ variables: { transactionId: transaction.id } });
  }, [getTransactionImages, transaction.id]);

  const amount = sumBy(transaction.park?.policyRateDetails || [], "price");
  const startDate = transaction.sessionStartedAtLocal ? new Date(transaction.sessionStartedAtLocal) : null;
  const endDate = transaction.sessionEndedAtLocal ? new Date(transaction.sessionEndedAtLocal) : null;
  const vehicleStatus = getVehicleStatus(registeredVehicle.accounts);
  const city =
    transaction.address_city && transaction.address_state
      ? `${transaction.address_city}, ${transaction.address_state}`
      : "-";

  const slides: CarouselSlide[] = (
    [
      startDate && imagesData?.transactionImages?.licensePlateImageUrl
        ? {
            title: "License Plate Capture",
            date: startDate,
            imageSrc: imagesData.transactionImages.licensePlateImageUrl,
          }
        : null,
      startDate && imagesData?.transactionImages?.sessionStartImageUrl
        ? { title: "Park Start", date: startDate, imageSrc: imagesData.transactionImages.sessionStartImageUrl }
        : null,
      endDate && imagesData?.transactionImages?.sessionEndImageUrl
        ? { title: "Park End", date: endDate, imageSrc: imagesData.transactionImages.sessionEndImageUrl }
        : null,
    ] as (CarouselSlide | null)[]
  ).filter((v): v is CarouselSlide => v !== null);

  return (
    <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}>
      {/* Top zone: image carousel or error */}
      {imagesError ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
          <LocalLevelError onTryAgain={() => getTransactionImages({ variables: { transactionId: transaction.id } })} />
        </Box>
      ) : (
        <ImageCarousel slides={slides} loading={imagesLoading} />
      )}

      <Divider />

      {/* Bottom zone: details grid + amount + nav */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          justifyContent: "space-between",
          p: 2,
          gap: 3,
        }}
      >
        {/* 2-column on mobile, 3-column on desktop */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
            gap: "12px 32px",
            flex: 1,
          }}
        >
          <DetailField label="Date" value={startDate ? format(startDate, "M/d/yyyy") : "-"} />
          <DetailField label="Location" value={transaction.address_street || "-"} />
          <DetailField label="Vehicle" value={`${registeredVehicle.state_code} ${registeredVehicle.license_plate}`} />
          <DetailField
            label="Time"
            value={startDate && endDate ? `${convertTimeToString(startDate)} – ${convertTimeToString(endDate)}` : "-"}
          />
          <DetailField label="City" value={city} />
          <Box>
            <Typography variant="body2">Vehicle Status:</Typography>
            <Chip
              label={vehicleStatus}
              size="small"
              sx={{
                backgroundColor: vehicleStatus === "Registered" ? "rgba(76, 175, 80, 0.1)" : "rgba(237, 108, 2, 0.1)",
                color: vehicleStatus === "Registered" ? "#2E7D32" : "#E65100",
                fontWeight: 500,
              }}
            />
          </Box>
        </Box>

        {/* Amount + nav: side-by-side at bottom on mobile, stacked on right on desktop */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            alignItems: { xs: "center", md: "flex-end" },
            justifyContent: { xs: "space-between", md: "flex-start" },
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
            <Typography variant="body2">Amount:</Typography>
            <Typography variant="body1" fontWeight={700}>
              ${formatCurrency(amount)}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" endIcon={<ArrowForwardIcon />} onClick={onClickViewDetails}>
            View full details
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

/** Reusable labelled field for the details grid. */
function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="body2">{label}:</Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}

export default InvoiceParkEventCard;
